import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from "@mui/material";
import { User } from "../../types/api";
import { AGENT_TRIAL_MODAL } from "../../constants";
import { ROUTES } from "../../constants/routes";

/**
 * Props for the AgentTrialModal component
 */
interface AgentTrialModalProps {
  user: User | null;
}

/**
 * AgentTrialModal component displays a one-time modal inviting users to try
 * the agentic AI tool for bio-based materials discovery.
 *
 * The modal:
 * - Shows only once per user and never reappears once dismissed
 * - Cannot be skipped (no backdrop click or escape key close)
 * - Has "No thanks" button to close without action
 * - Has "Yes" button to navigate to the agent mode
 *
 * Note: This component should only be rendered when the feature flag is enabled
 * (controlled by the parent component).
 *
 * @param props - The component props
 * @param props.user - The current authenticated user, or null if not logged in
 * @returns The AgentTrialModal component or null if not visible
 */
const AgentTrialModal: React.FC<AgentTrialModalProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  /**
   * Initializes the modal visibility when the component mounts or when the user changes.
   *
   * Checks the stored modal state in localStorage to determine if the modal should be shown.
   * If the modal was previously dismissed, it remains hidden permanently.
   */
  useEffect(() => {
    // Only show modal if user is logged in
    if (user?._id) {
      const modalState = localStorage.getItem(AGENT_TRIAL_MODAL.STATE_KEY);

      // If modal was already dismissed, keep it hidden permanently
      if (modalState === "dismissed") {
        setIsOpen(false);
        return;
      }

      // Otherwise show the modal
      setIsOpen(true);
    }
  }, [user?._id]);

  /**
   * Handles closing the modal and storing the dismissed state in localStorage.
   * Sets the modal as closed and stores the "dismissed" state permanently.
   */
  const handleDismiss = () => {
    setIsOpen(false);
    localStorage.setItem(AGENT_TRIAL_MODAL.STATE_KEY, "dismissed");
  };

  /**
   * Handles the "Yes" button click.
   * Dismisses the modal and navigates to the Materiom AI page with agent mode.
   */
  const handleAccept = () => {
    navigate(`${ROUTES.MATERIOM_AI}?mode=agent`);
    handleDismiss();
  };

  // Don't render if user is not logged in
  if (!user) return null;

  return (
    <Dialog
      open={isOpen}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          onClick: (e) => e.stopPropagation(),
        },
      }}
    >
      <DialogTitle>You're invited!</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Want to join a limited trial of the latest agentic AI tool for
          bio-based materials discovery?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} disableRipple>
          No thanks
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disableRipple
        >
          Yes!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentTrialModal;
